import { Box, Flex, Text } from "@chakra-ui/react";
import React from "react";

type StepState = "completed" | "active" | "upcoming";

type SubStepDefinition = {
  title: string;
  description: string;
};

type StepDefinition = {
  label: string;
  description?: string;
  subSteps?: SubStepDefinition[];
};

const AGENTIC_INTAKE_STEPS: StepDefinition[] = [
  {
    label: "Start",
    description: "Configure your AI interviewer and interview guide.",
  },
  {
    label: "Training",
    description: "Add materials to help make your interviewer smarter.",
  },
  {
    label: "Guide",
    subSteps: [
      {
        title: "",
        description: "Hang on while we generate your interview guide.",
      },
      {
        title: "",
        description: "Review your interview questions and evaluation criteria.",
      },
    ],
  },
  {
    label: "Customization",
    subSteps: [
      {
        title: "Options",
        description:
          "Add company info for candidate questions, set interview intro/outro scripts, and choose interviewer settings.",
      },
      {
        title: "Invites",
        description: "Personalize candidate invite & reminder emails.",
      },
      {
        title: "Test interviews",
        description:
          "Take the interview yourself and when you're satisfied, save or launch your interviewer.",
      },
    ],
  },
  {
    label: "Done",
  },
];

interface AgenticIntakeVerticalStepperProps {
  /** Zero-based index of the current step (0–4) */
  currentStep: number;
  /** Zero-based sub-step within the current step (0 for steps without sub-steps) */
  currentSubStep?: number;
  /** When true, renders only numbered circles with no labels or descriptions */
  collapsed?: boolean;
  /** Displayed as the header text; falls back to "NEW AI INTERVIEWER" on step 0 */
  jobTitle?: string;
}

const AgenticIntakeVerticalStepper: React.FC<
  AgenticIntakeVerticalStepperProps
> = ({ currentStep, currentSubStep = 0, collapsed = false, jobTitle }) => {
  const headerText =
    currentStep === 0 ? "NEW AI INTERVIEWER" : jobTitle || undefined;

  return (
    <Flex
      direction="column"
      w={collapsed ? "64px" : "180px"}
      flexShrink={0}
      pl="24px"
      mr="16px"
      overflowY="auto"
    >
      {!collapsed && headerText && (
        <Text
          fontFamily="Inter, sans-serif"
          fontSize="11.67px"
          fontWeight={600}
          lineHeight="16px"
          color="#808B97"
          textTransform="uppercase"
          textAlign="left"
        >
          {headerText}
        </Text>
      )}

      <Flex direction="column" mt="8px">
        {AGENTIC_INTAKE_STEPS.map((step, index) => {
          const isFirst = index === 0;
          const isLast = index === AGENTIC_INTAKE_STEPS.length - 1;
          const state: StepState =
            index < currentStep
              ? "completed"
              : index === currentStep
              ? "active"
              : "upcoming";

          const activeSubStep =
            state === "active" && step.subSteps
              ? step.subSteps[currentSubStep]
              : undefined;

          const activeDescription =
            state === "active"
              ? activeSubStep
                ? activeSubStep.description
                : step.description
              : undefined;

          const activeSubStepTitle =
            state === "active" && activeSubStep?.title
              ? activeSubStep.title
              : undefined;

          return (
            <Flex key={step.label} direction="row" align="stretch">
              <Flex direction="column" align="center" w="24px" flexShrink={0}>
                <Box
                  w="2px"
                  h="24px"
                  bg={isFirst ? "transparent" : "gray.200"}
                  flexShrink={0}
                />
                <StepCircle state={state} stepNumber={index + 1} />
                {!isLast && <Box w="2px" flex="1" minH="24px" bg="gray.200" />}
              </Flex>

              {!collapsed && (
                <Flex
                  direction="column"
                  ml="8px"
                  pt="28px"
                  pb={isLast ? "0" : "8px"}
                >
                  <Text
                    fontFamily="Inter, sans-serif"
                    fontSize="11.67px"
                    fontWeight={state === "active" ? 600 : 400}
                    lineHeight="16px"
                    color={
                      state === "active"
                        ? "#2DB282"
                        : state === "upcoming"
                        ? "gray.300"
                        : "gray.400"
                    }
                    whiteSpace="nowrap"
                  >
                    {step.label}
                  </Text>

                  {state === "active" && activeSubStepTitle && (
                    <Text
                      fontFamily="Inter, sans-serif"
                      fontSize="11.67px"
                      fontWeight={600}
                      lineHeight="16px"
                      color="#393F47"
                      mt="8px"
                    >
                      {activeSubStepTitle}
                    </Text>
                  )}

                  {state === "active" && activeDescription && (
                    <Text
                      fontFamily="Inter, sans-serif"
                      fontSize="11.67px"
                      fontWeight={400}
                      lineHeight="16px"
                      color="#393F47"
                      mt={activeSubStepTitle ? "4px" : "8px"}
                    >
                      {activeDescription}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

type StepCircleProps = {
  state: StepState;
  stepNumber: number;
};

const StepCircle: React.FC<StepCircleProps> = ({ state, stepNumber }) => {
  const isActive = state === "active";
  const isCompleted = state === "completed";
  const isGreen = isActive;

  return (
    <Flex
      align="center"
      justify="center"
      borderRadius="full"
      w="24px"
      h="24px"
      flexShrink={0}
      bg={isGreen ? "#60CCA5" : isCompleted ? "gray.200" : "transparent"}
      border="1.5px solid"
      borderColor={isGreen ? "#60CCA5" : "gray.200"}
    >
      <Text
        fontFamily="Inter, sans-serif"
        fontSize="9.72px"
        fontWeight={500}
        lineHeight="12px"
        color={
          isGreen || isCompleted
            ? "white"
            : state === "upcoming"
            ? "gray.300"
            : "gray.400"
        }
        textAlign="center"
      >
        {stepNumber}
      </Text>
    </Flex>
  );
};

export { AGENTIC_INTAKE_STEPS, AgenticIntakeVerticalStepper };
export type {
  AgenticIntakeVerticalStepperProps,
  StepDefinition,
  SubStepDefinition,
};
